import { useEffect, useState, Ref, useRef } from 'react';
import {
    Image,
    Popover,
    PopoverTrigger,
    Button,
    PopoverContent
} from '@chakra-ui/react';
import { Portal } from '@chakra-ui/portal';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import useStore from '../stores/store';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { MotionBox } from './UiElements';
import layoutStore from '../stores/layoutStore';
import { isFreeUser } from './userApi';
import getCustomEmojis from '../Utils/getCustomEmojis';
import { findGroup, findTab } from '../Utils/findDataFunctions';

interface EmojiPickerProps {
    emoji: string;
    type: string;
    id: string;
    ml?: string;
    groupId?: string;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({
    emoji,
    type,
    id,
    ml = '',
    groupId
}) => {
    const emojiPickerOpen = layoutStore((state) => state.emojiPickerOpen);
    const setEmojiPickerOpen = layoutStore((state) => state.setEmojiPickerOpen);

    const { colorMode } = useColorMode();
    const [emojiToShow, setEmoji] = useState(emoji);
    useEffect(() => {
        setEmoji(emoji);
    }, [emoji]);

    //const [isOpen, setIsOpen] = useState(false);
    const open = () => setEmojiPickerOpen(id);
    const close = () => setEmojiPickerOpen(null);

    const isOpen = emojiPickerOpen === id;

    const hoverBgLight = {
        group: 'gray.100',
        workspace: 'gray.300',
        workspaceModal: 'gray.100',
        stack: 'gray.100'
    };

    const hoverButton = useColorModeValue(hoverBgLight[type], '#32363A');
    const emojiCategory = useColorModeValue('gray.200', '#FFF');
    const emojiPickerShadow = useColorModeValue(
        '0 12px 36px -8px rgba(48,49,53,0.36)',
        '0 4px 18px -2px rgba(0,0,0,0.80)'
    );

    const onEmojiClick = (emoji) => {
        let emojiText;
        if (emoji.native) {
            emojiText = emoji.native;
        } else if (emoji.src) {
            emojiText = emoji.src;
        }
        if (!emojiText) {
            return;
        }
        setEmoji(emojiText);
        if (type === 'group') {
            useStore.getState().updateGroup({ emoji: emojiText }, id);
        } else if (type === 'workspace' || type === 'workspaceModal') {
            useStore.getState().updateWsMeta({ emoji: emojiText }, id);
        } else if (type === 'stack') {
            // console.log('update item');
            let tabGroups = structuredClone(useStore.getState().tabGroups);
            let group = findGroup(tabGroups, groupId);
            let item = findTab(group, id);
            item.emoji = emojiText;
            useStore.getState().updateTabGroups(tabGroups);
        }
        close();
        //SetShowPicker(false);
    };

    const size = {
        group: '64px',
        workspace: '32px',
        workspaceModal: '72px',
        stack: '28px'
    };
    const fontSize = {
        group: '47px',
        workspace: '18px',
        workspaceModal: '48px',
        stack: '16px'
    };
    const imgSize = {
        group: '48px',
        workspace: '20px',
        workspaceModal: '48px',
        stack: '18px'
    };

    const rounded = {
        group: '14px',
        workspace: '8px',
        workspaceModal: '16px',
        stack: '7px'
    };
    const roundedImage = {
        group: '4px',
        workspace: '2px',
        workspaceModal: '4px',
        stack: '2px'
    };

    const buttonRef = useRef() as Ref<HTMLButtonElement>;

    const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });

    const popoverWidth = 354; // Width of the MotionBox
    const popoverHeight = 200; // Height of the MotionBox, adjust this value as needed

    const openAndCalculatePosition = (e) => {
        e.stopPropagation();
        const { x, y } = calculatePopoverPosition(buttonRef, popoverHeight);
        setPopoverPosition({ x, y });
        open();
    };

    return (
        <>
            <Button
                ref={buttonRef}
                p="0"
                _focus={{ outline: 'none' }}
                zIndex="2"
                /* onClick={(e) => {
                  e.stopPropagation();
                  SetShowPicker(!showPicker);
                }} */
                background="transparent"
                _hover={{ bg: hoverButton }}
                _active={{ transform: 'scale(0.9475)' }}
                //mt="0.5rem"
                fontSize={fontSize[type]}
                minWidth="none"
                height={size[type]}
                maxWidth={size[type]}
                minW={size[type]}
                minH={size[type]}
                rounded={rounded[type]}
                onClick={(e) => openAndCalculatePosition(e)}
                fontWeight="600"
            >
                {emojiToShow?.length > 10 ? (
                    <Image
                        src={emojiToShow}
                        boxSize={imgSize[type]}
                        overflow="hidden"
                        rounded={roundedImage[type]}
                    />
                ) : (
                    emojiToShow
                )}
            </Button>
            {isOpen && (
                <Portal>
                    <MotionBox
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        ml={ml}
                        zIndex={100}
                        rounded="12px"
                        borderWidth="0px"
                        _focus={{ outline: 'none' }}
                        boxShadow={emojiPickerShadow}
                        border="var(--chakra-colors-teBorder)"
                        w="354px"
                        overflow="hidden"
                        style={{
                            zIndex: 999999,
                            position: 'absolute',
                            top: popoverPosition.y,
                            left: popoverPosition.x
                        }}
                    >
                        <Picker
                            data={data}
                            emojiVersion={1}
                            color={emojiCategory}
                            // /* emojiButtonSize={30}
                            //     emojiSize={20}
                            // emojiButtonRadius={'4px'}
                            emojiButtonRadius={'8px'}
                            previewPosition={'none'}
                            title="Select a emoji"
                            emoji=""
                            native={true}
                            showPreview={false}
                            onEmojiSelect={onEmojiClick}
                            onClickOutside={close}
                            theme={colorMode === 'light' ? 'light' : 'dark'}
                            custom={!isFreeUser() ? getCustomEmojis() : []}
                        />
                    </MotionBox>
                </Portal>
            )}
        </>
    );
};

export default EmojiPicker;

const calculatePopoverPosition = (buttonRef, popoverHeight) => {
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const { bottom, left, width } = buttonRect;

    const padding = 8; // Padding around the screen
    const popoverWidth = 354; // Width of the MotionBox

    let x = left + window.scrollX + width / 2 - popoverWidth / 2; // Center the popover horizontally
    const y = bottom + window.scrollY + 4; // Position the popover just below the button

    // Adjust x if the popover would go off the left or right edge of the screen
    if (x < padding) {
        x = padding;
    } else if (x + popoverWidth > window.innerWidth - padding) {
        x = window.innerWidth - popoverWidth - padding;
    }

    return { x, y };
};
